import React, { useState, useRef } from 'react';

const concernIcons = {
  acne: { src: '/assets/skin-concerns/acne.png', alt: 'Acne icon' },
  dull_skin: { src: '/assets/skin-concerns/dull-skin.png', alt: 'Dull skin icon' },
  pigmentation: { src: '/assets/skin-concerns/pigmentation.png', alt: 'Pigmentation icon' },
  acne_scar: { src: '/assets/skin-concerns/acne-scars.png', alt: 'Acne scars icon' },
  open_pores: { src: '/assets/skin-concerns/open-pores.png', alt: 'Open pores icon' },
  normal_skin: { src: '/assets/skin-concerns/general-checkup.png', alt: 'General checkup icon' },
  uneven_skin_tone: { src: '/assets/skin-concerns/uneven-tone.png', alt: 'Uneven tone icon' },
  wrinkles: { src: '/assets/skin-concerns/wrinkles.png', alt: 'Wrinkles icon' },
  loose_skin: { src: '/assets/skin-concerns/loose-skin.png', alt: 'Loose skin icon' },
  dark_circles: { src: '/assets/skin-concerns/dark-circles.png', alt: 'Dark circles icon' }
};

const ConcernIconFrame = ({ selected, src, alt }) => (
  <div
    className={`w-10 h-10 rounded-xl border flex items-center justify-center flex-shrink-0 transition-colors ${
      selected ? 'bg-white border-white/40 shadow-sm' : 'bg-gray-50 border-gray-200'
    }`}
  >
    <img src={src} alt={alt} className="w-8 h-8 object-contain select-none" draggable="false" />
  </div>
);

const ConcernIcon = ({ type, selected }) => {
  const icon = concernIcons[type];

  if (!icon) {
    return null;
  }

  return <ConcernIconFrame selected={selected} src={icon.src} alt={icon.alt} />;
};

const ConditionPage = ({ nextStep, previousStep, updatePatientData, appState, setError }) => {
  const [formData, setFormData] = useState({
    primaryConcern: appState.patientData?.primaryConcern || '',
    visitPurpose: appState.patientData?.visitPurpose || '',
    conditionDuration: appState.patientData?.conditionDuration || ''
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Refs for auto-scroll
  const section2Ref = useRef(null);
  const section3Ref = useRef(null);

  // Options with icons
  const primaryConcernOptions = [
    { value: 'acne', label: 'Acne', icon: 'acne' },
    { value: 'dull_skin', label: 'Dull Skin', icon: 'dull_skin' },
    { value: 'pigmentation', label: 'Pigmentation', icon: 'pigmentation' },
    { value: 'acne_scar', label: 'Acne Scars', icon: 'acne_scar' },
    { value: 'open_pores', label: 'Open Pores', icon: 'open_pores' },
    { value: 'normal_skin', label: 'General Checkup', icon: 'normal_skin' },
    { value: 'uneven_skin_tone', label: 'Uneven Tone', icon: 'uneven_skin_tone' },
    { value: 'wrinkles', label: 'Wrinkles', icon: 'wrinkles' },
    { value: 'loose_skin', label: 'Loose Skin', icon: 'loose_skin' },
    { value: 'dark_circles', label: 'Dark Circles', icon: 'dark_circles' }
  ];

  const visitPurposeOptions = [
    { value: 'just_curious', label: 'Just Curious', description: 'I want to know more about my skin' },
    { value: 'second_opinion', label: 'Second Opinion', description: "I've seen a doctor, want AI perspective" },
    { value: 'pre_consultation', label: 'Pre-Consultation', description: 'Planning to visit a dermatologist' },
    { value: 'monitoring', label: 'Monitoring', description: 'Tracking an existing condition' },
    { value: 'doctor_referred', label: 'Doctor Referred', description: 'My doctor sent me here' }
  ];

  const conditionDurationOptions = [
    { value: 'recent', label: 'Recent', description: 'Last few weeks' },
    { value: 'few_months', label: 'Few Months', description: '3-6 months' },
    { value: 'long_term', label: 'Long Term', description: '6+ months' },
    { value: 'always', label: 'Always Had', description: 'As long as I remember' },
    { value: 'not_applicable', label: 'Not Sure', description: 'General checkup' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Auto-scroll to next section with slight delay for visual feedback
    if (field === 'primaryConcern' && section2Ref.current) {
      setTimeout(() => {
        section2Ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    }
    if (field === 'visitPurpose' && section3Ref.current) {
      setTimeout(() => {
        section3Ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.primaryConcern) {
      errors.primaryConcern = 'Please select your primary concern';
    }
    if (!formData.visitPurpose) {
      errors.visitPurpose = 'Please select your visit purpose';
    }
    if (!formData.conditionDuration) {
      errors.conditionDuration = 'Please select the condition duration';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError('Please complete all fields before continuing.');
      return;
    }

    setIsSubmitting(true);

    try {
      updatePatientData({
        primaryConcern: formData.primaryConcern,
        visitPurpose: formData.visitPurpose,
        conditionDuration: formData.conditionDuration
      });

      setTimeout(() => {
        setIsSubmitting(false);
        nextStep();
      }, 500);

    } catch (error) {
      setIsSubmitting(false);
      setError('Failed to save condition information. Please try again.');
    }
  };

  const isFormComplete = formData.primaryConcern && formData.visitPurpose && formData.conditionDuration;

  // Selection Chip Component
  const SelectionChip = ({ selected, onClick, icon, label, description, compact }) => (
    <button
      type="button"
      onClick={onClick}
      className={`
        relative overflow-hidden text-left transition-all duration-200 transform
        ${compact
          ? 'px-4 py-3 rounded-xl'
          : 'p-4 rounded-2xl'
        }
        ${selected
          ? 'bg-gray-900 text-white scale-[1.02] shadow-lg border-2 border-gray-900'
          : 'bg-white border-2 border-gray-200 hover:border-gray-400 hover:shadow-md active:scale-[0.98]'
        }
      `}
    >
      <div className={`flex items-center ${compact ? 'space-x-2' : 'space-x-3'}`}>
        {icon ? <ConcernIcon type={icon} selected={selected} /> : null}
        <div className="flex-1 min-w-0">
          <div className={`font-medium truncate ${compact ? 'text-sm' : ''}`}>{label}</div>
          {description && !compact && (
            <div className={`text-xs mt-0.5 truncate ${selected ? 'text-gray-300' : 'text-gray-500'}`}>
              {description}
            </div>
          )}
        </div>
      </div>

      {/* Selection checkmark */}
      {selected && (
        <div className="absolute top-2 right-2 w-5 h-5 bg-white rounded-full flex items-center justify-center">
          <svg className="w-3 h-3 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}
    </button>
  );

  // Question Section Component
  const QuestionSection = ({ title, subtitle, children, index, error }) => (
    <div
      className="animate-fade-in"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
      {children}
      {error && (
        <p className="text-sm text-red-600 mt-2">{error}</p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Header */}
      <div className="sticky top-0 bg-white/80 backdrop-blur-lg z-10 border-b border-gray-100">
        <div className="max-w-md mx-auto px-6 py-4">
          {/* Progress */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-500">Step 3 of 4</span>
            <span className="text-sm text-gray-400">Almost done!</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-gray-900 h-1.5 rounded-full transition-all duration-500"
              style={{ width: isFormComplete ? '100%' : '75%' }}
            ></div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-md mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Tell us about your skin
          </h1>
          <p className="text-gray-600">
            This helps our AI focus on what matters most to you
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-10">
          {/* Question 1: Primary Concern - Grid of chips */}
          <QuestionSection
            title="What's your main concern?"
            subtitle="Select the issue you'd like us to focus on"
            index={0}
            error={validationErrors.primaryConcern}
          >
            <div className="grid grid-cols-2 gap-3">
              {primaryConcernOptions.map((opt) => (
                <SelectionChip
                  key={opt.value}
                  selected={formData.primaryConcern === opt.value}
                  onClick={() => handleInputChange('primaryConcern', opt.value)}
                  icon={opt.icon}
                  label={opt.label}
                />
              ))}
            </div>
          </QuestionSection>

          {/* Question 2: Visit Purpose - Full width cards */}
          <div ref={section2Ref}>
            <QuestionSection
              title="What brings you here?"
              subtitle="This helps us tailor our recommendations"
              index={1}
              error={validationErrors.visitPurpose}
            >
              <div className="space-y-3">
                {visitPurposeOptions.map((opt) => (
                  <SelectionChip
                    key={opt.value}
                    selected={formData.visitPurpose === opt.value}
                    onClick={() => handleInputChange('visitPurpose', opt.value)}
                    label={opt.label}
                    description={opt.description}
                  />
                ))}
              </div>
            </QuestionSection>
          </div>

          {/* Question 3: Duration - Compact chips */}
          <div ref={section3Ref}>
            <QuestionSection
              title="How long have you had this?"
              index={2}
              error={validationErrors.conditionDuration}
            >
              <div className="flex flex-wrap gap-2">
                {conditionDurationOptions.map((opt) => (
                  <SelectionChip
                    key={opt.value}
                    selected={formData.conditionDuration === opt.value}
                    onClick={() => handleInputChange('conditionDuration', opt.value)}
                    label={opt.label}
                    compact
                  />
                ))}
              </div>
            </QuestionSection>
          </div>

          {/* Info Box */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 border border-blue-100">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-1 text-sm">Why we ask this</h3>
                <p className="text-blue-800 text-xs leading-relaxed">
                  Your answers help our AI provide personalized insights focused on your specific concerns.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 pt-4 pb-8">
            <button
              type="submit"
              disabled={isSubmitting || !isFormComplete}
              className={`
                w-full font-medium py-4 px-6 rounded-2xl transition-all duration-300 flex items-center justify-center
                ${isFormComplete
                  ? 'bg-gray-900 text-white hover:bg-gray-800 shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                  Processing...
                </>
              ) : (
                <>
                  Start AI Analysis
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={previousStep}
              disabled={isSubmitting}
              className="w-full text-gray-500 font-medium py-3 px-4 transition-all duration-200 hover:text-gray-700"
            >
              Back
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConditionPage;
